'use client';

import './duel.css';
import {
  useEffect,
  useLayoutEffect,
  useState,
  type CSSProperties,
} from 'react';
import type {
  AssembledDuel,
  DuelBoardResponse,
  DuelRoundView,
} from '../lib/types';
import { DUEL_ROUNDS, DUEL_MAX_SCORE } from '../lib/types';
import { KeepPlayingFooter } from '../../_components/KeepPlayingFooter';
import { DailySlateNav } from '../../_components/DailySlateNav';
import { useShareResult } from '@/lib/gameShare';

const useIsoLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// Shared button styling (native <button> doesn't inherit font-family).
const BTN_PRIMARY: CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontWeight: 600,
  fontSize: 14,
  padding: '9px 16px',
  borderRadius: 8,
  cursor: 'pointer',
  border: '1px solid var(--coral-deep)',
  background: 'var(--coral-deep)',
  color: '#fff',
};

// Per-round pick state: rounds 0-8 hold 'a'|'b', index 9 holds the Call stat id.
type Phase = 'pick' | 'reveal' | 'done';

interface PlayState {
  idx: number; // 0..8 duels, 9 = The Call
  phase: Phase;
  picks: string[];
}

export function DuelBoard({
  board,
  userId,
}: {
  board: DuelBoardResponse;
  userId?: string;
}) {
  const { puzzle } = board;
  const [state, setState] = useState<PlayState>(() =>
    board.result
      ? { idx: DUEL_ROUNDS, phase: 'done', picks: board.result.picksLog }
      : { idx: 0, phase: 'pick', picks: [] },
  );
  const [streak, setStreak] = useState<number | null>(null);
  const [best, setBest] = useState<number | null>(
    board.result?.disciplineBest ?? null,
  );

  // Anonymous rehydrate: signed-in players get board.result from the server;
  // anon completions live only in localStorage (mirrors ConnectionsBoard).
  useIsoLayoutEffect(() => {
    if (board.result) return;
    try {
      const raw = window.localStorage.getItem(`duel_result_${puzzle.id}`);
      if (!raw) return;
      const saved = JSON.parse(raw) as { picks: string[] };
      if (Array.isArray(saved.picks) && saved.picks.length) {
        setState({ idx: DUEL_ROUNDS, phase: 'done', picks: saved.picks });
      }
    } catch {
      /* corrupt cache — play fresh */
    }
  }, [board.result, puzzle.id]);

  const roundCorrect = (i: number, picks: string[]) =>
    picks[i] === puzzle.rounds[i]?.winner;
  const callWon = (picks: string[]) =>
    puzzle.call.stats.some((s) => s.id === picks[DUEL_ROUNDS] && s.player_wins);
  const scoreOf = (picks: string[]) =>
    puzzle.rounds.reduce(
      (n, _r, i) => n + (roundCorrect(i, picks) ? 1 : 0),
      0,
    ) + (callWon(picks) ? 2 : 0);

  const done = state.phase === 'done';
  const picks = state.picks;
  const score = done ? scoreOf(picks) : null;

  function pickSide(side: 'a' | 'b') {
    if (state.phase !== 'pick' || state.idx >= DUEL_ROUNDS) return;
    setState((s) => ({ ...s, phase: 'reveal', picks: [...s.picks, side] }));
  }

  function pickCallStat(statId: string) {
    if (state.phase !== 'pick' || state.idx !== DUEL_ROUNDS) return;
    const nextPicks = [...state.picks, statId];
    setState({ idx: DUEL_ROUNDS, phase: 'reveal', picks: nextPicks });
  }

  function next() {
    if (state.phase !== 'reveal') return;
    if (state.idx < DUEL_ROUNDS) {
      const nextIdx = state.idx + 1;
      if (nextIdx <= DUEL_ROUNDS - 1) {
        setState((s) => ({ ...s, idx: nextIdx, phase: 'pick' }));
        return;
      }
      // advance into The Call
      setState((s) => ({ ...s, idx: DUEL_ROUNDS, phase: 'pick' }));
      return;
    }
    // Call revealed → game over
    void finish(state.picks);
    setState((s) => ({ ...s, phase: 'done' }));
  }

  async function finish(finalPicks: string[]) {
    try {
      const headers: Record<string, string> = {
        'content-type': 'application/json',
      };
      if (userId) headers['X-User-Id'] = userId;
      const res = await fetch('/api/spot/duel/result', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          puzzleId: puzzle.id,
          sessionKey: getSessionKey(),
          picks: finalPicks,
        }),
      });
      if (res.ok) {
        const data = (await res.json()) as {
          streak?: number;
          disciplineBest?: number | null;
        };
        if (typeof data.streak === 'number' && data.streak > 0)
          setStreak(data.streak);
        if (typeof data.disciplineBest === 'number')
          setBest(data.disciplineBest);
      }
    } catch {
      /* best-effort; the game already played out client-side */
    }
    try {
      window.localStorage.setItem(
        `duel_result_${puzzle.id}`,
        JSON.stringify({ picks: finalPicks }),
      );
    } catch {
      /* private mode / quota — non-fatal */
    }
  }

  return (
    <main className="dl-stage">
      <header className="dl-head">
        <div className="dl-kicker">spec duel · tue &amp; fri</div>
        <h1 className="dl-title">
          Spec <em>Duel</em>
        </h1>
        <div className="dl-banner">
          Duel №{String(puzzle.number).padStart(3, '0')} ·{' '}
          {puzzle.discipline.label} · score /{DUEL_MAX_SCORE}
        </div>
        {/* Rules show only until the first pick — after that they're read,
            and on a phone their ~70px is what pushes the reveal + advance
            button below the fold. */}
        {!done && picks.length === 0 && (
          <div className="dl-rules">
            <span>
              <b>Two cars, one stat — call the winner.</b> Nine duels, one point
              each.
            </span>
            <span>
              <b>Then The Call, worth 2:</b> pick the one stat this car beats a
              hidden rival on.
            </span>
          </div>
        )}
      </header>

      <div className="dl-board">
        <ProgressDots
          idx={state.idx}
          phase={state.phase}
          picks={picks}
          correct={roundCorrect}
          callWon={callWon}
        />

        {!done && state.idx < DUEL_ROUNDS && (
          <RoundCard
            round={puzzle.rounds[state.idx]}
            question={puzzle.discipline.question}
            revealed={state.phase === 'reveal'}
            pick={picks[state.idx] as 'a' | 'b' | undefined}
            onPick={pickSide}
          />
        )}

        {!done && state.idx === DUEL_ROUNDS && (
          <CallCard
            puzzle={puzzle}
            revealed={state.phase === 'reveal'}
            pick={picks[DUEL_ROUNDS]}
            onPick={pickCallStat}
          />
        )}

        {!done && state.phase === 'reveal' && (
          <div className="dl-next">
            <button onClick={next} style={BTN_PRIMARY}>
              {state.idx < DUEL_ROUNDS - 1
                ? 'Next duel →'
                : state.idx === DUEL_ROUNDS - 1
                  ? 'The Call →'
                  : 'See your score →'}
            </button>
          </div>
        )}

        {done && score !== null && (
          <>
            <EndScreen
              puzzle={puzzle}
              picks={picks}
              score={score}
              correct={roundCorrect}
              callWon={callWon}
              streak={streak}
              best={best}
              date={board.date}
            />
            <KeepPlayingFooter
              game="duel"
              date={board.date}
              userId={userId}
              archiveHref="/games?game=duel"
              includeSelf
            />
          </>
        )}
        <DailySlateNav currentGame="duel" date={board.date} userId={userId} />
      </div>
    </main>
  );
}

function ProgressDots({
  idx,
  phase,
  picks,
  correct,
  callWon,
}: {
  idx: number;
  phase: Phase;
  picks: string[];
  correct: (i: number, picks: string[]) => boolean;
  callWon: (picks: string[]) => boolean;
}) {
  const dots = [];
  for (let i = 0; i < DUEL_ROUNDS; i++) {
    const answered = picks.length > i && (i < idx || phase !== 'pick');
    dots.push(
      <span
        key={i}
        style={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          display: 'inline-block',
          background: answered
            ? correct(i, picks)
              ? 'var(--dl-win)'
              : 'var(--dl-loss)'
            : i === idx && phase === 'pick'
              ? 'var(--coral-deep)'
              : 'var(--dl-rule)',
        }}
      />,
    );
  }
  const callAnswered = picks.length > DUEL_ROUNDS;
  dots.push(
    <span
      key="call"
      title="The Call — worth 2"
      style={{
        width: 12,
        height: 12,
        transform: 'rotate(45deg)',
        display: 'inline-block',
        borderRadius: 3,
        background: callAnswered
          ? callWon(picks)
            ? 'var(--dl-win)'
            : 'var(--dl-loss)'
          : idx === DUEL_ROUNDS && phase === 'pick'
            ? 'var(--coral-deep)'
            : 'var(--dl-rule)',
      }}
    />,
  );
  return (
    <div
      style={{
        display: 'flex',
        gap: 7,
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 20,
      }}
    >
      {dots}
    </div>
  );
}

function RoundCard({
  round,
  question,
  revealed,
  pick,
  onPick,
}: {
  round: DuelRoundView;
  question: string;
  revealed: boolean;
  pick?: 'a' | 'b';
  onPick: (side: 'a' | 'b') => void;
}) {
  const maxValue = Math.max(round.a.value, round.b.value);
  const tile = (side: 'a' | 'b') => {
    const car = round[side];
    const isWinner = round.winner === side;
    const isPick = pick === side;
    const outcome = revealed
      ? isWinner
        ? 'win'
        : isPick
          ? 'loss'
          : undefined
      : undefined;
    return (
      <button
        key={car.id}
        onClick={() => onPick(side)}
        disabled={revealed}
        className="dl-tile"
        data-outcome={outcome}
      >
        <span className="dl-tile-name">{car.label}</span>
        {revealed && (
          <span className="dl-tile-val" data-outcome={outcome}>
            {car.display} {isWinner ? '✓' : isPick ? '✗' : ''}
          </span>
        )}
        {revealed && (
          <div className="dl-bar-track">
            <div
              className="dl-bar"
              style={{
                width: `${Math.max(6, Math.round((car.value / maxValue) * 100))}%`,
                background: isWinner ? 'var(--dl-win)' : 'var(--coral-deep)',
                opacity: isWinner ? 1 : 0.45,
              }}
            />
          </div>
        )}
      </button>
    );
  };
  return (
    <div>
      <p className="dl-question">{question}</p>
      <div className="dl-faceoff">
        {tile('a')}
        <div className="dl-vs" aria-hidden>
          VS
        </div>
        {tile('b')}
      </div>
      {revealed && <p className="dl-fact">{round.fun_fact}</p>}
    </div>
  );
}

function CallCard({
  puzzle,
  revealed,
  pick,
  onPick,
}: {
  puzzle: AssembledDuel;
  revealed: boolean;
  pick?: string;
  onPick: (statId: string) => void;
}) {
  const { call } = puzzle;
  const picked = call.stats.find((s) => s.id === pick);
  const won = !!picked?.player_wins;
  return (
    <div>
      <p className="dl-call-kicker">Final round · The Call · worth 2</p>
      <p className="dl-question" style={{ margin: '6px 0 4px' }}>
        This car: {call.player.label}
        <span style={{ opacity: 0.6 }}> vs </span>
        {call.opponent.label}
      </p>
      <p className="dl-sub">Pick the one stat this car wins.</p>
      <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
        {call.stats.map((s) => {
          const isPick = pick === s.id;
          const outcome =
            revealed && isPick ? (s.player_wins ? 'win' : 'loss') : undefined;
          return (
            <button
              key={s.id}
              onClick={() => onPick(s.id)}
              disabled={revealed}
              className="dl-choice"
              data-outcome={outcome}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: 10,
                }}
              >
                <span style={{ fontWeight: 700, fontSize: 15 }}>{s.label}</span>
                <span style={{ fontSize: 14, whiteSpace: 'nowrap' }}>
                  <strong>{s.player_display}</strong>
                  <span style={{ opacity: 0.55 }}>
                    {' '}
                    vs {revealed ? s.opponent_display : '???'}
                  </span>
                  {revealed && isPick ? (s.player_wins ? ' ✓' : ' ✗') : ''}
                </span>
              </div>
            </button>
          );
        })}
      </div>
      {revealed && (
        <>
          <p
            style={{
              textAlign: 'center',
              marginTop: 14,
              fontWeight: 700,
              fontSize: 16,
              color: won ? 'var(--dl-win-ink)' : 'var(--dl-loss-ink)',
            }}
          >
            {won ? 'Called it — +2 points' : 'Wrong call — no points'}
          </p>
          <p className="dl-fact" style={{ marginTop: 8 }}>
            {call.fun_fact}
          </p>
        </>
      )}
    </div>
  );
}

function EndScreen({
  puzzle,
  picks,
  score,
  correct,
  callWon,
  streak,
  best,
  date,
}: {
  puzzle: AssembledDuel;
  picks: string[];
  score: number;
  correct: (i: number, picks: string[]) => boolean;
  callWon: (picks: string[]) => boolean;
  streak: number | null;
  best: number | null;
  date: string;
}) {
  // Colored squares, not ✓/✗ — emoji render as chunky tiles in Discord/
  // iMessage where text glyphs vanish, and it matches the family (Connections
  // shares a 🟨🟩🟦🟪 grid, Grid a 🟩🟨🟥 scorecard). The Call stays a doubled
  // pair (worth 2), space-separated so it reads as its own beat.
  const glyphs =
    puzzle.rounds.map((_r, i) => (correct(i, picks) ? '🟩' : '🟥')).join('') +
    ' ' +
    (callWon(picks) ? '🟩🟩' : '🟥🟥');
  const streakTag = streak && streak > 1 ? `\n🔥 ${streak}-day streak` : '';
  const text = `Spec Duel #${puzzle.number} — ${puzzle.discipline.label}\n${glyphs} ${score}/${DUEL_MAX_SCORE}${streakTag}\nhttps://namethecar.com/${date}`;
  const { share, copied } = useShareResult('duel', text);
  const perfect = score === DUEL_MAX_SCORE;
  return (
    <div style={{ marginTop: 20, textAlign: 'center' }}>
      <p className="dl-verdict">
        {perfect
          ? 'Flawless. 🏁'
          : score >= 8
            ? 'Sharp eye.'
            : score >= 5
              ? 'Respectable.'
              : 'Rough day at the strip.'}
      </p>
      <p style={{ marginTop: 4, fontWeight: 600, fontSize: 18 }}>
        {score} / {DUEL_MAX_SCORE}
      </p>
      <pre className="dl-glyphs">{glyphs}</pre>
      {best !== null && (
        <p className="dl-meta">
          Your best {puzzle.discipline.label} day: {Math.max(best, score)}/
          {DUEL_MAX_SCORE}
        </p>
      )}
      {streak && streak > 1 ? (
        <p style={{ fontSize: 14, marginTop: 4 }}>🔥 {streak}-day streak</p>
      ) : null}
      <button onClick={share} style={{ ...BTN_PRIMARY, marginTop: 10 }}>
        {copied ? 'Copied!' : 'Share result'}
      </button>
    </div>
  );
}

// Anonymous session key, persisted in localStorage (parity with the other
// games; the key name must match GAME_META.duel.sessionKey in dailyGames.ts).
function getSessionKey(): string {
  if (typeof window === 'undefined') return 'ssr';
  let k = window.localStorage.getItem('duel_session_key');
  if (!k) {
    k = crypto.randomUUID();
    window.localStorage.setItem('duel_session_key', k);
  }
  return k;
}
